import { createContext, FC, ReactElement, useCallback, useContext, useState } from 'react'
import config from '../../config'
import { create } from '../../externals/api/contact'
import { get as getFaqApi, Question } from '../../externals/api/faq'
import { getSignedUploadUrl, uploadToS3 } from '../../externals/api/upload'
import { useAlertHandler } from '../alertHandler'

interface IQuestion {
  faq: Question[];
  getFaq: () => Promise<Question[]>;
  createTicket: (body: string, files?: File[]) => Promise<void>
}

const ContactContext = createContext<IQuestion>({} as IQuestion)

export const useContact = (): IQuestion => {
  const context = useContext(ContactContext)
  if (!context) {
    throw new Error('useContact must be used within an ContactContext')
  }

  return context
}

export const ContactProvider: FC = ({ children }): ReactElement => {
  const [faq, setFaq] = useState<Question[]>([])
  const { showError } = useAlertHandler()

  const getFaq = useCallback(async (): Promise<Question[]> => {
    try {
      const response = await getFaqApi()
      setFaq(response)

      return response
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setFaq, showError])

  const createTicket = useCallback(async (body: string, files?: File[]): Promise<void> => {
    try {
      let filesUrls = [] as string[]

      if (files && files.length) {
        filesUrls = await Promise.all(files.map(async (file): Promise<string> => {
          const { headers, url } = await getSignedUploadUrl({
            genDiffFilename: false,
            filename: file.name,
            contentType: file.type,
            folder: '/contact-files/',
            environment: config.ENVIRONMENT
          })

          const newHeaders = {
            ...headers,
            'Content-Type': file.type
          }

          await uploadToS3(url, file, newHeaders)

          return url.split('?')[0]
        }))
      }

      await create({
        locale: 'pt-br',
        subject: 'Contato via Area Logada',
        body: body + '\n\n' + filesUrls.join('\n')
      })
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setFaq, showError])

  return (
    <ContactContext.Provider
      value={{
        faq,
        getFaq,
        createTicket
      }}
    >
      {children}
    </ContactContext.Provider>
  )
}
